import i18next from 'i18next';
import { UseFormSetValue } from 'react-hook-form';

import {
  Building,
  Floor,
  OpticalEndpoint,
  Stair,
  VerticalityFormInputs
} from '@features/siteStatusV2/types';
import { TranslationNamespaces } from '../../../../../locales/constants/translationNamespaces.enum';

const selectAll = i18next.t('OrderList.all', {
  ns: TranslationNamespaces.SITE_STATUS
});

export const handleBuildingChange = (
  building: string | undefined,
  buildings: Building[],
  setStairsList: React.Dispatch<React.SetStateAction<string[]>>,
  setFloorsList: React.Dispatch<React.SetStateAction<string[]>>,
  setOutletsList: React.Dispatch<React.SetStateAction<string[]>>,
  setValue: UseFormSetValue<VerticalityFormInputs>
) => {
  if (buildings) {
    if (building) {
      if (building === selectAll) {
        // Gather all stairs from all buildings
        const allStairsList = Array.from(
          new Set(buildings.flatMap((b: Building) => b.stairs.map((s: Stair) => s.name)))
        );
        setStairsList(allStairsList);
        setValue('stair', selectAll);

        // Gather all floors from all stairs
        const allFloorsList = Array.from(
          new Set(
            buildings.flatMap((b: Building) =>
              b.stairs.flatMap((s: Stair) => s.floors.map((f: Floor) => f.name))
            )
          )
        );
        setFloorsList(allFloorsList);
        setValue('floor', selectAll);

        // Gather all outlets from all floors
        const allOutletsList = Array.from(
          new Set(
            buildings.flatMap((b: Building) =>
              b.stairs.flatMap((s: Stair) =>
                s.floors.flatMap((f: Floor) => f.pto.map((l: OpticalEndpoint) => l.ptoReference))
              )
            )
          )
        );
        setOutletsList(allOutletsList);
        setValue('outlet', selectAll);
      } else {
        // Handle regular building selection
        const newStairsList = buildings
          .find((b: Building) => b.name === building)
          ?.stairs.map((s: Stair) => s.name);

        if (newStairsList) {
          setValue('stair', selectAll);
          setValue('floor', selectAll);
          setValue('outlet', selectAll);
          setStairsList(newStairsList);
          if (newStairsList.length === 1) {
            setValue('stair', newStairsList[0]);
          }
        }
      }
    }
  }
};

export const handleStairChange = (
  stair: string | undefined,
  building: string | undefined,
  buildings: Building[],
  setFloorsList: React.Dispatch<React.SetStateAction<string[]>>,
  setValue: UseFormSetValue<VerticalityFormInputs>
) => {
  if (buildings) {
    if (stair) {
      if (stair === selectAll) {
        // Gather all floors from the selected building
        const selectedBuilding = buildings.find((b: Building) => b.name === building);
        const newFloorsList = selectedBuilding?.stairs.flatMap((s: Stair) =>
          s.floors.map((f: Floor) => f.name)
        );

        if (newFloorsList) {
          setFloorsList(newFloorsList);
          setValue('floor', selectAll);
          setValue('outlet', selectAll);
        }
      } else {
        // Handle regular stair selection
        const selectedBuilding = buildings.find((b: Building) => b.name === building);
        const selectedStair = selectedBuilding?.stairs.find((s: Stair) => s.name === stair);
        const newFloorsList = selectedStair?.floors.map((f: Floor) => f.name);

        if (newFloorsList) {
          setFloorsList(newFloorsList);
          setValue('floor', selectAll);
          setValue('outlet', selectAll);

          if (newFloorsList.length === 1) {
            setValue('floor', newFloorsList[0]);
          }
        }
      }
    }
  }
};

export const handleFloorChange = (
  floor: string | undefined,
  building: string | undefined,
  stair: string | undefined,
  buildings: Building[],
  setOutletsList: React.Dispatch<React.SetStateAction<string[]>>,
  setValue: UseFormSetValue<VerticalityFormInputs>
) => {
  if (buildings) {
    if (floor) {
      if (floor === selectAll) {
        // Gather all outlets from the selected building and stair
        const selectedBuilding = buildings.find((b: Building) => b.name === building);
        const selectedStair = selectedBuilding?.stairs.find((s: Stair) => s.name === stair);
        const newOutletsList = selectedStair?.floors.flatMap((f: Floor) =>
          f.pto.map((l: OpticalEndpoint) => l.ptoReference)
        );

        const getAllOtoReferences = selectedStair
          ? newOutletsList
          : selectedBuilding?.stairs.flatMap((stair) =>
              stair.floors.flatMap((floor) => floor.pto.map((line) => line.ptoReference))
            );

        if (getAllOtoReferences) {
          setOutletsList(getAllOtoReferences);
          setValue('outlet', selectAll);
        }
      } else {
        // Handle regular floor selection
        const selectedBuilding = buildings.find((b: Building) => b.name === building);
        const selectedStair = selectedBuilding?.stairs.find((s: Stair) => s.name === stair);
        const selectedFloor = selectedStair?.floors.find((f: Floor) => f.name === floor);
        const newOutletsList = selectedFloor?.pto.map((l: OpticalEndpoint) => l.ptoReference);

        if (newOutletsList) {
          setOutletsList(newOutletsList);
          setValue('outlet', selectAll);

          if (newOutletsList.length === 1) {
            setValue('outlet', newOutletsList[0]);
          }
        }
      }
    }
  }
};
